import { useState } from 'react';
import { Maximize, X } from 'lucide-react';

interface PdfViewerProps {
  resource: {
    signedUrl?: string;
    thumbnailUrl?: string;
    mimeType?: string;
    name?: string;
    size?: number;
  };
}

const MAX_PDF_SIZE = 20_000_000; // 20 MB

export function PdfViewer({ resource }: PdfViewerProps) {
  const [fullScreen, setFullScreen] = useState(false);
  const isPdf = resource.mimeType === 'application/pdf';

  if (!isPdf) {
    return (
      <div className="flex h-[35rem] items-center justify-center">
        <iframe
          src={`${resource.signedUrl}#toolbar=0`}
          title={resource.name}
          className="size-full"
        />
      </div>
    );
  }

  return (
    <div className={`relative ${fullScreen ? 'fixed inset-0 z-50 bg-black flex items-center justify-center' : 'flex h-[35rem] items-center justify-center'}`}>
      {/* Thumbnail (non-fullscreen) */}
      {resource.thumbnailUrl && !fullScreen && (
        <img
          src={resource.thumbnailUrl}
          alt="preview"
          className="max-h-full max-w-full object-contain"
        />
      )}

      {/* PDF iframe (fullscreen, under 20MB) */}
      {fullScreen && resource.signedUrl && (resource.size ?? 0) <= MAX_PDF_SIZE && (
        <iframe
          src={resource.signedUrl}
          title={resource.name}
          className="size-full"
        />
      )}

      {/* Over 20MB message */}
      {fullScreen && isPdf && (resource.size ?? 0) > MAX_PDF_SIZE && (
        <p className="text-lg text-white">
          Can&apos;t view PDF files larger than 20MB, download file to view
        </p>
      )}

      {/* Toggle button */}
      <button
        type="button"
        onClick={() => setFullScreen((v) => !v)}
        className="absolute bottom-3 right-3 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 transition-colors"
      >
        {fullScreen ? <X className="size-5" /> : <Maximize className="size-5" />}
      </button>
    </div>
  );
}
